import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Lock, Users, Share2, ArrowRight, CheckCircle, 
  Play, Star, Eye, Settings, Bell, FileText, UserCheck,
  Globe, MessageSquare, Clock, Award, ChevronRight, Target
} from 'lucide-react';

const SecurityCollaboration = () => {
  const [activeFeature, setActiveFeature] = useState('access-control');

  const securityFeatures = [
    {
      id: 'access-control',
      name: 'Role-Based Access Control',
      icon: Lock,
      description: 'Granular permissions and security management',
      capabilities: [
        'User role management with hierarchical permissions',
        'Object-level security with field-level controls',
        'Dynamic permission assignment based on criteria',
        'API access control and rate limiting',
        'Multi-factor authentication support',
        'Single sign-on (SSO) integration'
      ],
      apiEndpoints: [
        { name: 'getOrgGroups', description: 'List all user groups and roles' },
        { name: 'createUpdateGroup', description: 'Create and manage user groups' },
        { name: 'deleteOrgGroup', description: 'Remove user groups safely' },
        { name: 'getOrgQueues', description: 'Manage work queues and assignments' }
      ],
      examples: [
        'Department-specific data access controls',
        'Client portal with restricted permissions',
        'Executive dashboard with sensitive data protection',
        'Vendor access with limited system visibility'
      ]
    },
    {
      id: 'sharing-rules',
      name: 'Sharing Rules & Policies',
      icon: Share2,
      description: 'Advanced data sharing and privacy controls',
      capabilities: [
        'Conditional sharing rules based on data criteria',
        'Cross-organizational data sharing policies',
        'Temporary access grants with expiration',
        'Data privacy compliance (GDPR, HIPAA, SOX)',
        'Audit trails for all sharing activities',
        'Automated access reviews and certifications'
      ],
      apiEndpoints: [
        { name: 'getAllSharingRules', description: 'List all sharing configurations' },
        { name: 'createUpdateSharingRule', description: 'Create and update sharing rules' },
        { name: 'deleteSharingRule', description: 'Remove sharing rules' },
        { name: 'getAvailableOrgObjects', description: 'Get objects available for sharing' }
      ],
      examples: [
        'Client data sharing agreements with partners',
        'Project-based access for external consultants',
        'Regional data access restrictions',
        'Time-limited access for auditors'
      ]
    },
    {
      id: 'collaboration',
      name: 'Team Collaboration',
      icon: Users,
      description: 'Built-in collaboration tools for team productivity',
      capabilities: [
        'Record-level discussions and comment threads',
        'Real-time notifications and activity feeds',
        'Document sharing and version control',
        'Task assignment and workflow collaboration',
        'Team spaces and project collaboration',
        'Integration with communication platforms'
      ],
      apiEndpoints: [
        { name: 'getPosts', description: 'Retrieve record discussions' },
        { name: 'savePostOnRecord', description: 'Create posts and comments' },
        { name: 'getComments', description: 'Get comment threads' },
        { name: 'saveCommentOnPost', description: 'Add comments to discussions' },
        { name: 'deletePost', description: 'Remove posts and comments' }
      ],
      examples: [
        'Project collaboration spaces with shared documents',
        'Customer service case discussions and handoffs',
        'Sales opportunity tracking with team input',
        'Cross-functional workflow collaboration'
      ]
    }
  ];

  const securityStandards = [

    { name: 'GDPR Compliant', icon: '🇪🇺', description: 'European data protection regulation' },
    { name: 'HIPAA Ready', icon: '🏥', description: 'Healthcare information privacy' },
    { name: 'SOX Compliant', icon: '📊', description: 'Financial reporting controls' },
    { name: 'PCI DSS', icon: '💳', description: 'Payment card industry security' }
  ];

  const collaborationTools = [
    {
      name: 'Discussion Threads',
      icon: MessageSquare,
      description: 'Record-level conversations',
      features: ['Threaded discussions', 'Mentions and notifications', 'File attachments', 'Real-time updates']
    },
    {
      name: 'Activity Feeds',
      icon: Bell,
      description: 'Real-time activity tracking',
      features: ['Change notifications', 'User activity logs', 'Custom feed filters', 'Mobile notifications']
    },
    {
      name: 'Document Sharing',
      icon: FileText,
      description: 'Secure file collaboration',
      features: ['Version control', 'Access permissions', 'Preview capabilities', 'Download tracking']
    },
    {
      name: 'Team Workspaces',
      icon: Users,
      description: 'Collaborative project spaces',
      features: ['Shared dashboards', 'Team calendars', 'Resource libraries', 'Progress tracking']
    }
  ];

  const securityLayers = [
    {
      layer: 'Infrastructure Security',
      icon: '🏗️',
      features: ['Multi-region deployment', 'Data encryption at rest', 'Network security', 'DDoS protection']
    },
    {
      layer: 'Application Security',
      icon: '🔐',
      features: ['Secure coding practices', 'Regular security audits', 'Vulnerability scanning', 'Penetration testing']
    },
    {
      layer: 'Data Security',
      icon: '💾',
      features: ['End-to-end encryption', 'Data masking', 'Backup encryption', 'Secure deletion']
    },
    {
      layer: 'Access Security',
      icon: '🚪',
      features: ['Multi-factor authentication', 'SSO integration', 'Session management', 'Access monitoring']
    }
  ];

  const complianceFeatures = [
    {
      title: 'Audit & Compliance',
      description: 'Complete audit trails and compliance reporting',
      icon: Eye,
      capabilities: ['Complete activity logs', 'Compliance reporting', 'Data lineage tracking', 'Retention policies']
    },
    {
      title: 'Data Governance',
      description: 'Enterprise data governance and stewardship',
      icon: Settings,
      capabilities: ['Data classification', 'Governance policies', 'Data quality rules', 'Stewardship workflows']
    },
    {
      title: 'Risk Management',
      description: 'Proactive risk identification and mitigation',
      icon: Shield,
      capabilities: ['Risk assessments', 'Threat monitoring', 'Incident response', 'Security metrics']
    },
    {
      title: 'Privacy Controls',
      description: 'Advanced privacy protection and controls',
      icon: Lock,
      capabilities: ['Data anonymization', 'Consent management', 'Right to be forgotten', 'Privacy impact assessments']
    }
  ];

  const industryCompliance = [
    {
      industry: 'Healthcare',
      icon: '🏥',
      requirements: ['HIPAA compliance', 'Patient data protection', 'Audit trails', 'Access controls'],
      solutions: 'Complete HIPAA-ready platform with encrypted PHI handling'
    },
    {
      industry: 'Financial Services',
      icon: '🏦',
      requirements: ['SOX compliance', 'PCI DSS', 'Data encryption', 'Fraud prevention'],
      solutions: 'Bank-grade security with financial industry compliance'
    },
    {
      industry: 'Government',
      icon: '🏛️',
      requirements: ['FedRAMP ready', 'FISMA compliance', 'Data sovereignty', 'Security clearance'],
      solutions: 'Government-grade security with compliance frameworks'
    },
    {
      industry: 'Enterprise',
      icon: '🏢',
      requirements: ['GDPR', 'Enterprise SSO'],
      solutions: 'Enterprise security with global compliance standards'
    }
  ];

  const testimonials = [
    {
      quote: "The role-based access control gave us the granular security we needed for our healthcare data. HIPAA compliance was seamless.",
      name: "",
      title: "",
      company: "HealthFirst Systems",
      avatar: "👩‍⚕️",
      metric: "100% HIPAA compliant"
    },
    {
      quote: "Collaboration features transformed our remote team productivity. Real-time discussions on records eliminated endless email chains.",
      name: "",
      title: "",
      company: "Global Consulting",
      avatar: "👨‍💼",
      metric: "50% faster collaboration"
    },
    {
      quote: "Sharing rules allowed us to securely collaborate with external partners while maintaining strict data controls.",
      name: "",
      title: "",
      company: "Financial Corp",
      avatar: "👩‍💼",
      metric: "Zero security incidents"
    }
  ];

  const stats = [
    { number: '99.9%', label: 'Security Uptime', description: 'Enterprise-grade availability' },
    { number: '256-bit', label: 'Encryption Standard', description: 'Military-grade data protection' },
    { number: '24/7', label: 'Security Monitoring', description: 'Continuous threat detection' },
    { number: '0', label: 'Data Breaches', description: 'Perfect security record' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-red-900 via-pink-900 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white mb-6">
              <Shield className="h-10 w-10" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Security & Collaboration
              <span className="block text-3xl lg:text-4xl font-normal mt-2 text-red-200">
                Enterprise-Grade Protection
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 mb-10 leading-relaxed">
              Secure your business data with enterprise-grade security controls while enabling 
              seamless team collaboration and productivity.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Try Security Features
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="https://www.youtube.com/@nocodeAquargin"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Security Demo
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="text-4xl font-bold text-red-600 mb-2">{stat.number}</div>
                <div className="text-gray-900 font-semibold mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Collaboration Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Security & Collaboration Gallery
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our platform combines enterprise security with seamless collaboration
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/Crystalline_security_shields_surrounding_and_prote_1750595731.png"
                  alt="Enterprise Security Fortress"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Security Fortress</h3>
              <p className="text-gray-600 mt-2">Multi-layered security protection</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/Hierarchical_diagram_showing_different_user_roles_1750595798.png"
                  alt="Role-Based Access Control"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Role Management</h3>
              <p className="text-gray-600 mt-2">Granular access control systems</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/images/Modern_collaborative_workspace_with_diverse_team_m_1750595764.png"
                  alt="Team Collaboration"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Team Collaboration</h3>
              <p className="text-gray-600 mt-2">Secure collaborative workspaces</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Security Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Security & Collaboration Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive security controls with seamless collaboration capabilities
            </p>
          </div>

          {/* Feature Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {securityFeatures.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setActiveFeature(feature.id)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeFeature === feature.id
                    ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <feature.icon className="h-5 w-5" />
                <span>{feature.name}</span>
              </button>
            ))}
          </div>

          {/* Active Feature Content */}
          {securityFeatures.map((feature) => (
            activeFeature === feature.id && (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl mx-auto"
              >
                <div className="grid lg:grid-cols-2 gap-12">
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center mr-4">
                        <feature.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{feature.name}</h3>
                    </div>
                    <p className="text-xl text-gray-600 mb-8">{feature.description}</p>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        Key Capabilities
                      </h4>
                      <ul className="space-y-3">
                        {feature.capabilities.map((capability, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="bg-gray-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Settings className="h-5 w-5 text-red-500 mr-2" />
                        API Endpoints
                      </h4>
                      <div className="space-y-3">
                        {feature.apiEndpoints.map((endpoint, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border-l-4 border-red-500">
                            <div className="font-mono text-sm text-red-600 font-semibold">
                              {endpoint.name}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {endpoint.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-red-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Target className="h-5 w-5 text-red-500 mr-2" />
                        Use Case Examples
                      </h4>
                      <div className="space-y-3">
                        {feature.examples.map((example, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <ChevronRight className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-700">{example}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </div>
      </section>

      {/* Security Standards */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Compliance & Standards
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meeting the highest security and compliance standards globally
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {securityStandards.map((standard, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="text-4xl mb-3">{standard.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{standard.name}</h3>
                <p className="text-sm text-gray-600">{standard.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Collaboration Tools */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Collaboration Tools
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built-in tools that enhance team productivity and communication
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {collaborationTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center mr-4">
                    <tool.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{tool.name}</h3>
                    <p className="text-gray-600">{tool.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {tool.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Layers */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Multi-Layer Security
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Defense in depth with multiple security layers protecting your data
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {securityLayers.map((layer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{layer.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">{layer.layer}</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {layer.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Shield className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Governance & Compliance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive governance tools for enterprise compliance requirements
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {complianceFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center mr-4">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {feature.capabilities.map((capability, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Award className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{capability}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Compliance */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Industry-Specific Compliance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored security solutions for different industry requirements
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {industryCompliance.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{industry.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">{industry.industry}</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Requirements:</h4>
                    <div className="flex flex-wrap gap-2">
                      {industry.requirements.map((req, idx) => (
                        <span key={idx} className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                          {req}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Our Solution:</h4>
                    <p className="text-gray-600">{industry.solutions}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Security Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              How organizations secured their operations while enabling collaboration
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-3xl mr-3">{testimonial.avatar}</div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.title}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-red-600">{testimonial.metric}</div>
                    <div className="text-xs text-gray-500">Achievement</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Secure Your Business Today
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Experience enterprise-grade security with seamless collaboration. 
              Start your free trial and protect your business data.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Security Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Shield className="mr-2 h-5 w-5" />
                Schedule Security Demo
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No credit card required  ✓ Full security access  ✓ 24/7 support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SecurityCollaboration; 